import React from 'react';
import { AutocompleteSelect } from '@repo/ui/components/AutocompleteSelect';
import { cn } from '@repo/ui/utils';
import { FormField } from './FormField';

export interface FormAutocompleteSelectOption {
  key: string;
  label: React.ReactNode;
  onSelect: () => void;
  className?: string;
}

interface FormAutocompleteSelectFieldProps {
  label: string;
  id: string;
  error?: string | null;
  fieldClassName?: string;
  labelClassName?: string;
  className?: string;
  disabled?: boolean;
  onChange: (value: string) => void;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  onBlur?: () => void;
  required?: boolean;
  value: string;
  placeholder?: string;
  options: FormAutocompleteSelectOption[];
  isLoading?: boolean;
}

export const FormAutocompleteSelectField = React.forwardRef<
  HTMLInputElement,
  FormAutocompleteSelectFieldProps
>(
  (
    {
      label,
      id,
      error,
      fieldClassName,
      labelClassName,
      required,
      options,
      className,
      value,
      isLoading,
      ...autocompleteProps
    },
    ref
  ) => {
    const errorId = `${id}-error`;
    const hintId = `${id}-hint`;
    return (
      <FormField
        label={label}
        htmlFor={id}
        error={error}
        errorId={errorId}
        className={fieldClassName}
        labelClassName={labelClassName}
        required={required}
      >
        <AutocompleteSelect
          ref={ref}
          id={id}
          value={value}
          aria-required={required}
          aria-describedby={[hintId, error ? errorId : null]
            .filter(Boolean)
            .join(' ')}
          className={cn(
            error && '[&_input]:border-destructive-ghost-foreground',
            className
          )}
          {...autocompleteProps}
        >
          <AutocompleteSelect.Options isLoading={isLoading}>
            {options.map((option) => (
              <AutocompleteSelect.Option
                key={option.key}
                onSelect={option.onSelect}
                className={option.className}
                selected={option.key === value}
              >
                {option.label}
              </AutocompleteSelect.Option>
            ))}
          </AutocompleteSelect.Options>
        </AutocompleteSelect>
        <span id={hintId} className="sr-only">
          Use the down arrow key to browse available options.
        </span>
      </FormField>
    );
  }
);
FormAutocompleteSelectField.displayName = 'FormAutocompleteSelectField';
